import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { api, COMPANY } from "../lib/site";
import { useLang } from "../i18n/LanguageContext";

export default function Feedback() {
  const { t } = useLang();
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, lead: null });
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [publish, setPublish] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/feedback/${token}`);
        setState({ loading: false, error: null, lead: r.data });
        setName(r.data?.name || "");
        setDone(!!r.data?.already_submitted);
      } catch (e) { setState({ loading: false, error: e?.response?.status === 404 ? t("fb.invalid") : t("fb.loadErr"), lead: null }); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/feedback/${token}`, { rating, text, name, publish });
      setDone(true);
      toast.success(t("fb.thanksToast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("fb.sendErr"));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo title={`${t("fb.eyebrow")} | ${COMPANY.name}`} description={t("fb.body")} path={`/tilbakemelding-skjema/${token}`} noindex />
      <Header />
      <section className="section">
        <div className="container-page max-w-2xl">
          {state.loading && <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-[#1B3626]" /></div>}
          {state.error && !state.loading && (
            <div className="bg-white border border-[#E2DDD5] rounded-2xl p-8 text-center">
              <div className="font-display text-2xl font-bold">{state.error}</div>
              <Link to="/" className="btn-primary mt-6 inline-flex">{t("fb.toHome")}</Link>
            </div>
          )}
          {state.lead && !state.error && done && (
            <div className="bg-white border-2 border-[#1B3626] rounded-2xl p-8 text-center" data-testid="feedback-done">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#EEF4F0] grid place-items-center mb-4"><CheckCircle2 className="text-[#1B3626]" size={38} /></div>
              <div className="font-display text-2xl font-bold text-[#1B3626]">{t("fb.doneTitle")}</div>
              <p className="mt-2 text-[#1C1F22]">{t("fb.doneBody")}</p>
              <a href={`tel:${COMPANY.phoneClean}`} className="btn-secondary mt-6 inline-flex" data-testid="feedback-call"><Phone size={14}/> {t("hero.callBtn")} {COMPANY.phone}</a>
            </div>
          )}
          {state.lead && !state.error && !done && (
            <div className="bg-white border border-[#E2DDD5] rounded-2xl p-6 sm:p-8">
              <span className="eyebrow">{t("fb.eyebrow")}</span>
              <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("fb.title")}</h1>
              <div className="dot-divider mt-4" />
              <p className="mt-5 text-[#383E45]">{t("fb.greeting")} {state.lead.name} — {t("fb.body")}</p>
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="feedback-form">
                <div>
                  <div className="text-sm font-semibold text-[#1C1F22] mb-2">{t("fb.rating")}</div>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => setRating(n)} className="p-1" data-testid={`fb-star-${n}`}>
                        <Star size={34} className={n <= rating ? "text-[#E8B547] fill-[#E8B547]" : "text-[#D5D0C7]"} />
                      </button>
                    ))}
                  </div>
                </div>
                <label className="block"><span className="text-sm font-semibold text-[#1C1F22]">{t("fb.comment")}</span>
                  <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("fb.commentPh")} className="mt-1.5 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="fb-text" />
                </label>
                <label className="block"><span className="text-sm font-semibold text-[#1C1F22]">{t("fb.namePubl")}</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="fb-name" />
                </label>
                <label className="flex items-start gap-2.5 text-sm text-[#383E45]">
                  <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="mt-1" data-testid="fb-publish" />
                  <span>{t("fb.publishOk")}</span>
                </label>
                <button type="submit" disabled={saving} className="btn-primary w-full sm:w-auto" data-testid="fb-submit">{saving ? <Loader2 size={14} className="animate-spin"/> : t("fb.submit")}</button>
              </form>
            </div>
          )}
        </div>
      </section>
      <Header />
      <section className="section">
        <div className="container-page max-w-2xl">
          {state.loading && <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-[#1B3626]" /></div>}
          {state.error && !state.loading && (
            <div className="bg-white border border-[#E2DDD5] rounded-2xl p-8 text-center">
              <div className="font-display text-2xl font-bold">{state.error}</div>
              <Link to="/" className="btn-primary mt-6 inline-flex">Til forsiden</Link>
            </div>
          )}
          {state.lead && !state.error && done && (
            <div className="bg-white border-2 border-[#1B3626] rounded-2xl p-8 text-center" data-testid="feedback-done">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#EEF4F0] grid place-items-center mb-4"><CheckCircle2 className="text-[#1B3626]" size={38} /></div>
              <div className="font-display text-2xl font-bold text-[#1B3626]">Tusen takk!</div>
              <p className="mt-2 text-[#1C1F22]">Vi setter stor pris på din tilbakemelding.</p>
              <a href={`tel:${COMPANY.phoneClean}`} className="btn-secondary mt-6 inline-flex" data-testid="feedback-call"><Phone size={14}/> Ring {COMPANY.phone}</a>
            </div>
          )}
          {state.lead && !state.error && !done && (
            <div className="bg-white border border-[#E2DDD5] rounded-2xl p-6 sm:p-8">
              <span className="eyebrow">Tilbakemelding</span>
              <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">Hvordan var jobben?</h1>
              <div className="dot-divider mt-4" />
              <p className="mt-5 text-[#383E45]">
                Hei {state.lead.name} — vi håper du er fornøyd med {state.lead.service ? <><strong>{state.lead.service}</strong>-oppdraget </> : "arbeidet "} vi utførte {state.lead.location ? `i ${state.lead.location}` : ""}. Din tilbakemelding hjelper oss å bli bedre.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="feedback-form">
                <div>
                  <div className="text-sm font-semibold text-[#1C1F22] mb-2">Din rangering</div>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => setRating(n)} className="p-1" data-testid={`fb-star-${n}`}>
                        <Star size={34} className={n <= rating ? "text-[#E8B547] fill-[#E8B547]" : "text-[#D5D0C7]"} />
                      </button>
                    ))}
                  </div>
                </div>
                <label className="block"><span className="text-sm font-semibold text-[#1C1F22]">Kommentar</span>
                  <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Fortell oss gjerne litt mer om opplevelsen..." className="mt-1.5 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="fb-text" />
                </label>
                <label className="block"><span className="text-sm font-semibold text-[#1C1F22]">Navn (vises hvis publisert)</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="fb-name" />
                </label>
                <label className="flex items-start gap-2.5 text-sm text-[#383E45]">
                  <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="mt-1" data-testid="fb-publish" />
                  <span>Ja, dere kan bruke tilbakemeldingen som kundeomtale på nettsiden. (Vises først etter godkjenning av administrator.)</span>
                </label>
                <button type="submit" disabled={saving} className="btn-primary w-full sm:w-auto" data-testid="fb-submit">{saving ? <Loader2 size={14} className="animate-spin"/> : "Send inn"}</button>
              </form>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
